/**
 * Siege Game Engine
 *
 * Handles the core game loop, physics, hit detection, and game state.
 * All coordinates are normalized (0-1) for resolution independence.
 */

import { createUnit, applyDifficultyScaling } from './unit-types.js';

// Base position (center of screen)
const BASE_POSITION = { x: 0.5, y: 0.5 };
const BASE_RADIUS = 0.05;
const CAPTURE_RADIUS = 0.08;

/**
 * Game Engine class
 */
export class SiegeGameEngine {
    constructor(options = {}) {
        // Callbacks
        this.onStateChange = options.onStateChange || (() => {});
        this.onUnitDestroyed = options.onUnitDestroyed || (() => {});
        this.onBaseHit = options.onBaseHit || (() => {});
        this.onGameOver = options.onGameOver || (() => {});
        this.onWaveComplete = options.onWaveComplete || (() => {});

        // Game configuration
        this.config = {
            difficulty: 'normal',
            clickDamage: 25,
            clickAoeRadius: 0.04,
            baseHealth: 100,
            ...options.config
        };

        // Game state
        this.reset();

        // Animation frame
        this.animationFrameId = null;
        this.lastFrameTime = 0;
    }

    /**
     * Reset game state to initial values
     */
    reset() {
        this.units = [];
        this.effects = [];  // Visual effects (explosions, etc.)
        this.baseHealth = this.config.baseHealth;
        this.maxBaseHealth = this.config.baseHealth;
        this.score = 0;
        this.wave = 0;
        this.isRunning = false;
        this.isPaused = false;
        this.cursorPosition = { x: 0.5, y: 0.5 };
        this.lastClickTime = 0;
        this.stats = {
            unitsKilled: 0,
            damageDealt: 0,
            clicksFired: 0
        };
    }

    /**
     * Start the game loop
     */
    start() {
        if (this.isRunning) return;

        this.isRunning = true;
        this.isPaused = false;
        this.lastFrameTime = performance.now();

        this.gameLoop(this.lastFrameTime);
        this.notifyStateChange();
    }

    /**
     * Pause the game
     */
    pause() {
        this.isPaused = true;
        this.notifyStateChange();
    }

    /**
     * Resume the game
     */
    resume() {
        this.isPaused = false;
        this.lastFrameTime = performance.now();
        this.gameLoop(this.lastFrameTime);
        this.notifyStateChange();
    }

    /**
     * Stop the game
     */
    stop() {
        this.isRunning = false;
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        this.notifyStateChange();
    }

    /**
     * Main game loop
     * @param {number} currentTime - Current timestamp
     */
    gameLoop(currentTime) {
        if (!this.isRunning || this.isPaused) return;

        const deltaTime = (currentTime - this.lastFrameTime) / 1000; // Convert to seconds
        this.lastFrameTime = currentTime;

        // Update game state
        this.update(deltaTime);

        // Schedule next frame
        this.animationFrameId = requestAnimationFrame((t) => this.gameLoop(t));
    }

    /**
     * Update game state
     * @param {number} deltaTime - Time since last frame (seconds)
     */
    update(deltaTime) {
        // Clamp deltaTime to avoid huge jumps if tab was inactive
        deltaTime = Math.min(deltaTime, 0.1);

        // Update units
        this.updateUnits(deltaTime);

        // Update base (check for capture)
        this.updateBase(deltaTime);

        // Update effects
        this.updateEffects(deltaTime);

        // Notify state change
        this.notifyStateChange();
    }

    /**
     * Update all units
     * @param {number} deltaTime - Time since last frame
     */
    updateUnits(deltaTime) {
        const unitsToRemove = [];
        const unitsToAdd = [];

        for (const unit of this.units) {
            if (unit.isDead) {
                unitsToRemove.push(unit);
                continue;
            }

            // Calculate direction to base
            const dx = BASE_POSITION.x - unit.x;
            const dy = BASE_POSITION.y - unit.y;
            const dist = Math.hypot(dx, dy);

            // Check if unit reached capture radius
            if (dist < CAPTURE_RADIUS) {
                unit.isCapturing = true;
            } else {
                unit.isCapturing = false;

                // Move toward base
                const moveX = (dx / dist) * unit.speed * deltaTime;
                const moveY = (dy / dist) * unit.speed * deltaTime;
                unit.x += moveX;
                unit.y += moveY;
            }

            // Handle healer units
            if (unit.healRadius > 0 && unit.healRate > 0) {
                this.applyHealerEffect(unit, deltaTime);
            }

            // Handle boss minion spawning
            if (unit.spawnMinions && !unit.isDead) {
                const now = Date.now();
                if (now - unit.lastMinionSpawn > unit.spawnMinions.interval) {
                    unit.lastMinionSpawn = now;
                    // Spawn minions around the boss
                    for (let i = 0; i < unit.spawnMinions.count; i++) {
                        const angle = (Math.PI * 2 * i) / unit.spawnMinions.count;
                        const spawnDist = 0.05;
                        const newUnit = createUnit(
                            unit.spawnMinions.type,
                            unit.x + Math.cos(angle) * spawnDist,
                            unit.y + Math.sin(angle) * spawnDist
                        );
                        if (newUnit) {
                            unitsToAdd.push(applyDifficultyScaling(newUnit, this.config.difficulty));
                        }
                    }
                }
            }
        }

        // Remove dead units and add new ones
        this.units = this.units.filter(u => !unitsToRemove.includes(u));
        this.units.push(...unitsToAdd);
    }

    /**
     * Apply healer effect to nearby units
     * @param {Unit} healer - Healer unit
     * @param {number} deltaTime - Time since last frame
     */
    applyHealerEffect(healer, deltaTime) {
        const healAmount = healer.healRate * deltaTime;

        for (const unit of this.units) {
            if (unit === healer || unit.isDead) continue;

            const dist = Math.hypot(unit.x - healer.x, unit.y - healer.y);
            if (dist < healer.healRadius) {
                unit.hp = Math.min(unit.maxHp, unit.hp + healAmount);
            }
        }
    }

    /**
     * Update base state
     * @param {number} deltaTime - Time since last frame
     */
    updateBase(deltaTime) {
        const capturingUnits = this.units.filter(u => u.isCapturing && !u.isDead);

        if (capturingUnits.length > 0) {
            // Calculate total damage
            const totalDamage = capturingUnits.reduce((sum, u) => sum + u.damage, 0);
            const damage = totalDamage * deltaTime;

            this.baseHealth -= damage;
            this.onBaseHit(damage, this.baseHealth);

            // Check for game over
            if (this.baseHealth <= 0) {
                this.baseHealth = 0;
                this.onGameOver('base_captured');
                this.stop();
            }
        }
    }

    /**
     * Update visual effects
     * @param {number} deltaTime - Time since last frame
     */
    updateEffects(deltaTime) {
        this.effects = this.effects.filter(effect => {
            effect.age += deltaTime;
            return effect.age < effect.duration;
        });
    }

    /**
     * Handle cursor position update from desktop
     * @param {number} x - Normalized X (0-1)
     * @param {number} y - Normalized Y (0-1)
     */
    updateCursor(x, y) {
        this.cursorPosition = { x, y };
    }

    /**
     * Handle cursor click (weapon fire)
     * @param {number} x - Normalized X (0-1)
     * @param {number} y - Normalized Y (0-1)
     */
    handleClick(x, y) {
        if (!this.isRunning || this.isPaused) return;

        this.stats.clicksFired++;
        this.lastClickTime = Date.now();

        // Add click effect
        this.effects.push({
            type: 'click',
            x,
            y,
            radius: this.config.clickAoeRadius,
            age: 0,
            duration: 0.3
        });

        // Check for hits (AOE damage)
        const hitRadius = this.config.clickAoeRadius;
        const damage = this.config.clickDamage;

        for (const unit of this.units) {
            if (unit.isDead) continue;

            const dist = Math.hypot(unit.x - x, unit.y - y);
            // Hit detection: click AOE overlaps with unit hitbox
            if (dist < hitRadius + unit.size) {
                // Calculate damage falloff (more damage at center)
                const falloff = 1 - (dist / (hitRadius + unit.size));
                const actualDamage = damage * Math.max(0.3, falloff);

                this.damageUnit(unit, actualDamage);
            }
        }
    }

    /**
     * Apply damage to a unit
     * @param {Unit} unit - Unit to damage
     * @param {number} amount - Damage amount
     */
    damageUnit(unit, amount) {
        if (unit.isDead) return;

        unit.hp -= amount;
        this.stats.damageDealt += amount;

        if (unit.hp <= 0) {
            this.destroyUnit(unit);
        }
    }

    /**
     * Destroy a unit
     * @param {Unit} unit - Unit to destroy
     */
    destroyUnit(unit) {
        unit.isDead = true;
        this.stats.unitsKilled++;
        this.score += unit.points;

        // Add death effect
        this.effects.push({
            type: 'explosion',
            x: unit.x,
            y: unit.y,
            radius: unit.size * 2,
            color: unit.color,
            age: 0,
            duration: 0.5
        });

        // Handle spawn on death (splitters)
        if (unit.spawnOnDeath) {
            const { type, count } = unit.spawnOnDeath;
            for (let i = 0; i < count; i++) {
                const angle = (Math.PI * 2 * i) / count;
                const offset = 0.03;
                const newUnit = createUnit(
                    type,
                    unit.x + Math.cos(angle) * offset,
                    unit.y + Math.sin(angle) * offset
                );
                if (newUnit) {
                    this.units.push(applyDifficultyScaling(newUnit, this.config.difficulty));
                }
            }
        }

        this.onUnitDestroyed(unit);
    }

    /**
     * Spawn a unit
     * @param {string} typeId - Unit type ID
     * @param {number} x - X position
     * @param {number} y - Y position
     * @returns {Unit|null}
     */
    spawnUnit(typeId, x, y) {
        const unit = createUnit(typeId, x, y);
        if (!unit) return null;

        const scaledUnit = applyDifficultyScaling(unit, this.config.difficulty);
        this.units.push(scaledUnit);
        return scaledUnit;
    }

    /**
     * Spawn a unit from a random edge
     * @param {string} typeId - Unit type ID
     * @returns {Unit|null}
     */
    spawnUnitFromEdge(typeId) {
        // Pick a random edge (0=top, 1=right, 2=bottom, 3=left)
        const edge = Math.floor(Math.random() * 4);
        let x, y;

        // Add some margin to spawn just outside visible area
        const margin = 0.05;

        switch (edge) {
            case 0: // Top
                x = Math.random();
                y = -margin;
                break;
            case 1: // Right
                x = 1 + margin;
                y = Math.random();
                break;
            case 2: // Bottom
                x = Math.random();
                y = 1 + margin;
                break;
            case 3: // Left
                x = -margin;
                y = Math.random();
                break;
        }

        return this.spawnUnit(typeId, x, y);
    }

    /**
     * Start a new wave
     * @param {number} waveNumber - Wave number (1-indexed)
     * @param {Array} unitSpecs - Array of { type, count } objects
     */
    startWave(waveNumber, unitSpecs) {
        this.wave = waveNumber;

        // Clear existing units (optional - could let them persist)
        // this.units = [];

        // Spawn units with slight delays
        let delay = 0;
        for (const spec of unitSpecs) {
            for (let i = 0; i < spec.count; i++) {
                setTimeout(() => {
                    if (this.isRunning) {
                        this.spawnUnitFromEdge(spec.type);
                    }
                }, delay);
                delay += 200 + Math.random() * 300; // Stagger spawns
            }
        }

        this.notifyStateChange();
    }

    /**
     * Check if current wave is complete (no units alive)
     * @returns {boolean}
     */
    isWaveComplete() {
        return this.units.filter(u => !u.isDead).length === 0;
    }

    /**
     * Get current game state for rendering
     * @returns {Object}
     */
    getState() {
        return {
            isRunning: this.isRunning,
            isPaused: this.isPaused,
            wave: this.wave,
            score: this.score,
            baseHealth: this.baseHealth,
            maxBaseHealth: this.maxBaseHealth,
            basePosition: BASE_POSITION,
            baseRadius: BASE_RADIUS,
            captureRadius: CAPTURE_RADIUS,
            units: this.units.filter(u => !u.isDead).map(u => ({
                id: u.id,
                type: u.typeId,
                typeId: u.typeId,
                x: u.x,
                y: u.y,
                hp: u.hp,
                maxHp: u.maxHp,
                size: u.size,
                color: u.color,
                isCapturing: u.isCapturing
            })),
            effects: this.effects,
            cursorPosition: this.cursorPosition,
            stats: { ...this.stats },
            config: { ...this.config }
        };
    }

    /**
     * Update game configuration
     * @param {Object} newConfig - Configuration updates
     */
    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
    }

    /**
     * Notify state change callback
     */
    notifyStateChange() {
        this.onStateChange(this.getState());
    }
}

export default SiegeGameEngine;
